import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  ALLOWED_DOMAINS,
  refreshEmailVerified,
  resendVerificationEmail,
  sendPasswordReset,
  signInWithEmail,
  signInWithGoogle,
  signOut,
  signUpWithEmail,
} from "../lib/firebase";
import { useAuth } from "../lib/auth";

type Mode = "signin" | "signup";

export default function LoginPage() {
  const { user, loading, domainOk, emailVerified } = useAuth();

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;
  if (user && domainOk && emailVerified) return <Navigate to="/" replace />;
  if (user && domainOk && !emailVerified) return <VerifyEmailGate email={user.email!} />;

  return <LoginForm />;
}

function LoginForm() {
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<"google" | "email" | null>(null);
  const [showReset, setShowReset] = useState(false);

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-4">
      <div className="card p-8 w-full max-w-sm">
        <h1 className="text-xl font-semibold">Document Review</h1>
        <p className="mt-1 text-sm text-slate-500">Sign in with your work account.</p>

        <button
          className="btn-primary w-full justify-center mt-6"
          disabled={busy !== null}
          onClick={async () => {
            setErr(null);
            setBusy("google");
            try {
              await signInWithGoogle();
            } catch (e) {
              setErr(e instanceof Error ? e.message : String(e));
            } finally {
              setBusy(null);
            }
          }}
        >
          <img src="/v_transparent.png" alt="" className="h-4 w-4 object-contain" />
          Sign in with Google
          <span className="ml-1 text-xs text-white/70">@chicagoventure.com</span>
        </button>

        <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
          <div className="h-px flex-1 bg-slate-200" />
          OR
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <CapitalLawHeader />
        <EmailPasswordForm
          busy={busy === "email"}
          onBusy={(b) => setBusy(b ? "email" : null)}
          onError={setErr}
        />

        <div className="mt-3 text-center">
          <button
            className="text-xs text-slate-500 hover:text-ink hover:underline"
            onClick={() => setShowReset(true)}
          >
            Forgot password?
          </button>
        </div>

        {err && <p className="mt-4 text-sm text-red-600">{err}</p>}
      </div>

      {showReset && <PasswordResetModal onClose={() => setShowReset(false)} />}
    </div>
  );
}

function CapitalLawHeader() {
  return (
    <div className="rounded-md bg-[#1d1d1d] px-3 py-2 mb-3 flex items-center gap-2 text-sm">
      <img src="/capital-law.png" alt="Capital Law" className="h-5 object-contain" />
      <span className="text-xs text-white/70 ml-auto">@capital-law.com</span>
    </div>
  );
}

function EmailPasswordForm({
  busy,
  onBusy,
  onError,
}: {
  busy: boolean;
  onBusy: (b: boolean) => void;
  onError: (msg: string | null) => void;
}) {
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [info, setInfo] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    onError(null);
    setInfo(null);
    onBusy(true);
    try {
      if (mode === "signin") {
        await signInWithEmail(email.trim(), password);
      } else {
        await signUpWithEmail(email.trim(), password, name.trim() || undefined);
        setInfo("Account created. Check your email for a verification link.");
      }
    } catch (e) {
      onError(prettyAuthError(e));
    } finally {
      onBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-2">
      {mode === "signup" && (
        <input
          className="input"
          placeholder="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      )}
      <input
        type="email"
        className="input"
        placeholder="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        autoComplete="email"
      />
      <input
        type="password"
        className="input"
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        minLength={mode === "signup" ? 8 : undefined}
        autoComplete={mode === "signin" ? "current-password" : "new-password"}
      />
      <button className="btn-secondary w-full justify-center" type="submit" disabled={busy}>
        {busy ? "…" : mode === "signin" ? "Sign in" : "Create account"}
      </button>
      <div className="text-center text-xs text-slate-500 pt-1">
        {mode === "signin" ? (
          <>
            Don't have an account?{" "}
            <button
              type="button"
              className="text-ink hover:underline"
              onClick={() => {
                setMode("signup");
                onError(null);
                setInfo(null);
              }}
            >
              Create one
            </button>
          </>
        ) : (
          <>
            Already have an account?{" "}
            <button
              type="button"
              className="text-ink hover:underline"
              onClick={() => {
                setMode("signin");
                onError(null);
                setInfo(null);
              }}
            >
              Sign in
            </button>
          </>
        )}
      </div>
      {info && <p className="text-sm text-emerald-700">{info}</p>}
    </form>
  );
}

function VerifyEmailGate({ email }: { email: string }) {
  const [busy, setBusy] = useState<"resend" | "check" | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-4">
      <div className="card p-8 w-full max-w-sm">
        <h1 className="text-lg font-semibold">Verify your email</h1>
        <p className="mt-2 text-sm text-slate-600">
          We sent a verification link to <span className="font-medium">{email}</span>.
        </p>
        <ol className="mt-3 text-sm text-slate-600 list-decimal pl-5 space-y-1">
          <li>Open the email and click the link.</li>
          <li>Come back here and click <span className="font-medium">I've verified</span> below.</li>
        </ol>
        <button
          className="btn-primary w-full justify-center mt-5"
          disabled={busy !== null}
          onClick={async () => {
            setErr(null);
            setInfo(null);
            setBusy("check");
            try {
              if (await refreshEmailVerified()) {
                // Fresh token now carries email_verified — reload into the app.
                window.location.assign("/");
              } else {
                setErr(
                  "Still not verified. Make sure you clicked the link in the email, then try again.",
                );
              }
            } catch (e) {
              setErr(e instanceof Error ? e.message : String(e));
            } finally {
              setBusy(null);
            }
          }}
        >
          {busy === "check" ? "Checking…" : "I've verified — continue"}
        </button>
        <button
          className="btn-secondary w-full justify-center mt-2"
          disabled={busy !== null}
          onClick={async () => {
            setErr(null);
            setInfo(null);
            setBusy("resend");
            try {
              await resendVerificationEmail();
              setInfo("Verification email re-sent. It can take a minute to arrive.");
            } catch (e) {
              setErr(e instanceof Error ? e.message : String(e));
            } finally {
              setBusy(null);
            }
          }}
        >
          {busy === "resend" ? "Sending…" : "Resend verification email"}
        </button>
        <button
          className="text-xs text-slate-500 hover:text-ink hover:underline w-full text-center mt-3"
          disabled={busy !== null}
          onClick={() => signOut()}
        >
          Sign out
        </button>
        {info && <p className="mt-3 text-sm text-emerald-700">{info}</p>}
        {err && <p className="mt-3 text-sm text-red-600">{err}</p>}
      </div>
    </div>
  );
}

function PasswordResetModal({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-20">
      <div className="card w-full max-w-sm p-6">
        <h2 className="font-semibold text-lg mb-2">Reset password</h2>
        <p className="text-sm text-slate-600 mb-3">
          Enter your email and we'll send you a reset link. Allowed domains:{" "}
          {ALLOWED_DOMAINS.map((d) => "@" + d).join(", ")}.
        </p>
        <input
          type="email"
          className="input mb-3"
          placeholder="you@chicagoventure.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
        />
        {err && <p className="text-sm text-red-600 mb-2">{err}</p>}
        {info && <p className="text-sm text-emerald-700 mb-2">{info}</p>}
        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>
            Close
          </button>
          <button
            className="btn-primary"
            disabled={busy || !email}
            onClick={async () => {
              setErr(null);
              setInfo(null);
              setBusy(true);
              try {
                await sendPasswordReset(email.trim());
                setInfo("If that email is registered, a reset link is on its way.");
              } catch (e) {
                setErr(prettyAuthError(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Sending…" : "Send reset link"}
          </button>
        </div>
      </div>
    </div>
  );
}

function prettyAuthError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  // Firebase error codes look like: Firebase: Error (auth/invalid-credential).
  const m = msg.match(/auth\/([a-z-]+)/);
  const code = m?.[1];
  switch (code) {
    case "invalid-credential":
    case "wrong-password":
    case "user-not-found":
      return "Wrong email or password.";
    case "email-already-in-use":
      return "An account with that email already exists. Try signing in.";
    case "weak-password":
      return "Password must be at least 8 characters.";
    case "invalid-email":
      return "That doesn't look like a valid email address.";
    case "too-many-requests":
      return "Too many attempts. Wait a minute and try again.";
    case "network-request-failed":
      return "Network error. Check your connection.";
    default:
      return msg;
  }
}

